import type { Schemas } from '@argzombie/api-client';

/*
 * Save points (tester tools, `Dev:TesterTools`): pure helpers for `pages/SavePointsPage.tsx` and
 * `components/PlayerSavePoints.tsx`. The calls themselves go through the typed client in those files.
 */

export type DevSaveRow = Schemas['DevSaveRow'];
export type DevSaveLoadReport = Schemas['DevSaveLoadReport'];
export type DevSaveFile = Schemas['SaveFile'];
/**
 * `POST /dev/players/{id}/reset` modes (ADR 0069): `day1` deletes every character and creates character 1,
 * `newCharacter` (was `newRun`) adds one within the slot cap, `kill` turns the active character into a zombie,
 * `resurrect` brings the active zombie back for free. Day Zero (ADR 0074): `tutorialFresh` is day 1 with a full tutorial (day 1 also
 * switches the backfill off), `movementPassed` marks the movement beats passed and adds a character (a cache start), `graduated` lets
 * the active character skip Day Zero (no kit).
 */
export type ResetMode =
  | 'day1'
  | 'newCharacter'
  | 'kill'
  | 'resurrect'
  | 'tutorialFresh'
  | 'movementPassed'
  | 'graduated';

/** The button and result title of each reset mode. */
export const RESET_TITLES: Record<ResetMode, string> = {
  day1: 'Reset to day 1',
  newCharacter: 'New character',
  kill: 'Zombify the active character',
  resurrect: 'Resurrect free',
  tutorialFresh: 'Fresh Day Zero',
  movementPassed: 'Day Zero: cache start',
  graduated: 'Skip Day Zero',
};

/** Shown instead of the list when the API answers 404 `not_found` (no tester tools on this deployment). */
export const TESTER_TOOLS_NOTE =
  'Save points need tester tools (Dev:TesterTools), which only the dev deployment has.';

/** Thrown by a query whose endpoint answered 404 `not_found`: the server lacks `Dev:TesterTools`. */
export class TesterToolsMissing extends Error {
  constructor() {
    super(TESTER_TOOLS_NOTE);
    this.name = 'TesterToolsMissing';
  }
}

/** True for a 404 whose ProblemDetails code is `not_found` (tester tools off), not e.g. `unknown_save`. */
export function isTesterToolsOff(status: number, error: unknown): boolean {
  if (status !== 404) return false;
  const code = error && typeof error === 'object' ? (error as { code?: unknown }).code : undefined;
  return code === undefined || code === 'not_found';
}

/** "512 B", "3.4 KB", "1.2 MB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** The export's file name, the same slug the API puts in Content-Disposition ("before-boss-2.save.json"). */
export function saveFileName(name: string): string {
  const slug = [...name.toLowerCase()]
    .map((c) => (/^[a-z0-9]$/.test(c) ? c : '-'))
    .join('')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return `${slug || 'save'}.save.json`;
}

const AUTO_SAVE_NOTE =
  'An automatic save of their current state is taken first, so "Undo last load/reset" can bring it back.';

/** The confirm() text for loading a save onto a player. */
export function loadConfirmText(
  save: Pick<DevSaveRow, 'name' | 'sourceLabel' | 'sourcePlayerId'>,
  target: string,
  sameAsSource: boolean,
): string {
  const from = sameAsSource ? 'their own' : `${save.sourceLabel ?? save.sourcePlayerId}'s`;
  return [
    `Load "${save.name}" (${from} save) onto ${target}?`,
    `This replaces ${target}'s whole game state with the save.` +
      (sameAsSource ? '' : ' The save is remapped onto their account.'),
    AUTO_SAVE_NOTE,
  ].join('\n\n');
}

/** The confirm() text for a reset. */
export function resetConfirmText(mode: ResetMode, player: string): string {
  const what = {
    day1: `Reset ${player} to day 1? Every character is deleted and they become exactly a brand-new sign-up: one character, no home, nothing owned. Only the account itself is kept.`,
    newCharacter: `Add a new character for ${player}? It takes the next free slot (within the cap) and becomes active; the other characters, alive or zombie, stay as they are, and account progression is kept.`,
    kill: `Turn ${player}'s active character into a zombie? This is the in-game death (cause dev_reset): the run freezes, nothing is deleted.`,
    resurrect: `Resurrect ${player}'s active zombie for free? It comes back alive in place with the resurrection boost, and no credit is spent.`,
    tutorialFresh: `Reset ${player} to day 1 for a fresh Day Zero? Every character is deleted, as with day 1, and the account plays the full tutorial even if it is older than the release.`,
    movementPassed: `Mark ${player}'s account as past Day Zero's movement beats and add a new character? The new character starts Day Zero at the cache.`,
    graduated: `Let ${player}'s active character skip Day Zero? It is marked as graduated (no kit, no companion change), so raids, crafting and orders open.`,
  }[mode];
  return `${what}\n\n${AUTO_SAVE_NOTE}`;
}

/** The confirm() text for undo. */
export function undoConfirmText(player: string): string {
  return [
    `Undo the last load or reset for ${player}?`,
    'This loads their newest automatic save, replacing their current game state. Doing it again redoes (another automatic save is taken first).',
  ].join('\n\n');
}

/** The confirm() text for deleting a save. */
export function deleteConfirmText(save: Pick<DevSaveRow, 'name' | 'kind'>): string {
  return `Delete the ${save.kind} save "${save.name}"? This can't be undone (export it first to keep a copy).`;
}

/** "Removed 120 rows, inserted 98 · remapped onto another player · 2 warnings". */
export function reportSummary(r: DevSaveLoadReport): string {
  const parts = [`Removed ${r.rowsRemoved} rows, inserted ${r.rowsInserted}`];
  if (r.remapped) parts.push('remapped onto another player');
  if (r.warnings.length)
    parts.push(`${r.warnings.length} warning${r.warnings.length === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

export type ParsedSaveFile = { ok: true; file: DevSaveFile } | { ok: false; error: string };

/** Parses an exported `*.save.json`: JSON, an object with a `snapshot` holding `tables`. */
export function parseSaveFile(text: string): ParsedSaveFile {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return { ok: false, error: 'That file is not JSON.' };
  }
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return { ok: false, error: 'That file is not a save file (expected a JSON object).' };
  const snapshot = (value as { snapshot?: unknown }).snapshot;
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot))
    return { ok: false, error: 'That file has no "snapshot": export a save point to get one.' };
  const tables = (snapshot as { tables?: unknown }).tables;
  if (!tables || typeof tables !== 'object' || Array.isArray(tables))
    return { ok: false, error: 'The save file\'s snapshot has no "tables".' };
  return { ok: true, file: value as DevSaveFile };
}

/** The list filter: '' hides automatic saves (the default), 'all' shows everything, or one kind. */
export type KindFilter = '' | 'all' | 'manual' | 'import' | 'auto';

/** The API's `kind` query value for a filter (it matches one kind exactly; '' and 'all' send none). */
export const kindParam = (f: KindFilter): string | undefined =>
  f === '' || f === 'all' ? undefined : f;

/** Applies the client-side part of a filter: the default drops automatic saves. */
export const applyKindFilter = (rows: DevSaveRow[], f: KindFilter): DevSaveRow[] =>
  f === '' ? rows.filter((r) => r.kind !== 'auto') : rows;
